import { lazy, Suspense, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";

import { SettingsProvider } from "./context/SettingsContext";
import { AuthProvider } from "./context/AuthContext";
import { ModalProvider } from "./context/ModalContext";

import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { FloatingButtons } from "./components/FloatingButtons";
import { ScrollProgress } from "./components/ScrollProgress";
import { Loader } from "./components/Loader";
import { Modals } from "./components/modals/Modals";

import Home from "./pages/Home";
const AreaPage = lazy(() => import("./pages/AreaPage"));
const Contact = lazy(() => import("./pages/Contact"));
const BlogList = lazy(() => import("./pages/BlogList"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const LearningGuide = lazy(() => import("./pages/LearningGuide"));
const LessonServicePage = lazy(() => import("./pages/LessonServicePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));

const CRAWLER_PATTERN = /bot|crawler|spider|crawling|google|bing|yandex|baidu|duckduckgo|facebookexternalhit|linkedinbot|whatsapp/i;
const isCrawler = () => typeof navigator !== "undefined" && CRAWLER_PATTERN.test(navigator.userAgent || "");

function SiteChrome({ children }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  if (isAdmin) return children;
  return (
    <>
      <ScrollProgress />
      <Header />
      <main className="site-content">{children}</main>
      <Footer />
      <FloatingButtons />
      <Modals />
    </>
  );
}

function App() {
  const [appReady, setAppReady] = useState(isCrawler);
  const skipLoader = isCrawler();
  return (
    <div className="App">
      <BrowserRouter>
        <SettingsProvider>
          <AuthProvider>
            <ModalProvider>
              {!skipLoader && <Loader onComplete={() => setAppReady(true)} />}
              {appReady && <>
                <Toaster position="top-center" richColors />
                <SiteChrome>
                  <Suspense fallback={<main className="min-h-screen" aria-busy="true" />}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/automatic-driving-lessons-northolt" element={<LessonServicePage lessonType="automatic" />} />
                      <Route path="/manual-driving-lessons-northolt" element={<LessonServicePage lessonType="manual" />} />
                      <Route path="/driving-school-northolt" element={<AreaPage fixedSlug="northolt" />} />
                      <Route path="/driving-school-greenford" element={<AreaPage fixedSlug="greenford" />} />
                      <Route path="/driving-school-southall" element={<AreaPage fixedSlug="southall" />} />
                      <Route path="/driving-school-south-harrow" element={<AreaPage fixedSlug="south-harrow" />} />
                      <Route path="/areas/:slug" element={<AreaPage />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/blog" element={<BlogList />} />
                      <Route path="/blog/:slug" element={<BlogPost />} />
                      <Route path="/learn" element={<LearningGuide />} />
                      <Route path="/learn/:areaSlug" element={<LearningGuide />} />
                      <Route path="/learn/:areaSlug/:profileSlug" element={<LearningGuide />} />
                      <Route path="/learn/:areaSlug/:profileSlug/:skillSlug" element={<LearningGuide />} />
                      <Route path="/admin/login" element={<AdminLogin />} />
                      <Route path="/admin" element={<AdminDashboard />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </SiteChrome>
              </>}
            </ModalProvider>
          </AuthProvider>
        </SettingsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
